import { useState } from 'react'
import { Alert, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconRobot } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'

import { Callout, DataCard, EmptyState } from '@/components/ui'
import {
  AVATARES, AVATAR_LABEL, HARDWARE_COM_AVATAR, escolherAvatar, reciboDoDono, useMeuRobo,
  type Avatar, type AvisoDoAjuste, type Robo,
} from '@/api/robo'
import { ReciboDoAviso } from './ReciboDoAviso'
import { rotuloDoRobo } from './avisoDoAjuste'

/**
 * O quadro que o visor desenha, gerado do proprio codigo do firmware
 * (`devices/robo/AmazonasInnovare/tools/gerar_avatares.py`, spec §7.1).
 */
const foto = (a: Avatar) => `/robo/avatares/${a}.png`

export function MeuRoboAparencia() {
  const { robo, carregando } = useMeuRobo()
  if (carregando) return <Text size="sm" c="dimmed">Carregando…</Text>
  if (!robo) {
    return (
      <EmptyState
        icon={IconRobot}
        title="Você ainda não tem um robô"
        description="Robôs são atribuídos por quem administra a frota. Peça a quem cuida dos robôs do seu cliente."
      />
    )
  }
  return <AvataresDoRobo robo={robo} />
}

/**
 * A grade de avatares, sem o estado vazio da página: é o passo Aparência do
 * wizard de recebimento e a seção `#aparencia` da tela única (spec 2026-09-27).
 */
export function AvataresDoRobo({ robo }: { robo: Robo }) {
  const qc = useQueryClient()
  const [salvando, setSalvando] = useState<Avatar | null>(null)
  const [trocado, setTrocado] = useState<Avatar | null>(null)
  const [erro, setErro] = useState(false)
  const [aviso, setAviso] = useState<AvisoDoAjuste | null>(null)
  // A imagem e' arquivo estatico e pode faltar — se alguem apagar um arquivo
  // ou entrar personagem novo sem gerar o quadro. O ladrilho continua
  // clicavel: some a imagem, nao a escolha.
  const [semFoto, setSemFoto] = useState<Avatar[]>([])

  if (robo.hardware_model === null) {
    return (
      <DataCard title="Aparência">
        <Callout variant="info">
          O modelo deste robô ainda não foi informado por quem administra a
          frota, e é ele que decide se há avatares para escolher. Peça a
          atualização do cadastro — assim que o modelo entrar, esta tela passa
          a mostrar as opções.
        </Callout>
      </DataCard>
    )
  }

  if (robo.hardware_model !== HARDWARE_COM_AVATAR) {
    return (
      <DataCard title="Aparência">
        <Callout variant="info">
          Só o Robô II tem avatares: os personagens são desenhados no visor
          colorido, que o seu modelo não tem. O seu continua mostrando alertas
          e falando com você normalmente.
        </Callout>
      </DataCard>
    )
  }

  // Sem escolha gravada o firmware desenha o Inovare, entao e' ele que esta em
  // uso — marcar nada deixaria a tela dizendo menos do que o visor mostra.
  const atual: Avatar = robo.avatar ?? 'inovare'

  const trocar = async (a: Avatar) => {
    setErro(false)
    setTrocado(null)
    setAviso(null)
    setSalvando(a)
    try {
      const salvoAgora = await escolherAvatar(a)
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
      setTrocado(a)
      setAviso(salvoAgora.aviso ?? null)
    } catch {
      setErro(true)
    } finally {
      setSalvando(null)
    }
  }

  return (
    <Stack gap="md">
      <DataCard title="Aparência">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Escolha quem aparece no visor do seu robô. A troca vale só para ele.
          </Text>

          {erro && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              Não foi possível trocar o avatar agora. O visor continua com o
              anterior.
            </Alert>
          )}
          {trocado && !erro && (
            <Alert color="green" icon={<IconCheck size={18} />}>
              {AVATAR_LABEL[trocado]} escolhido. O visor troca em alguns
              segundos, na próxima vez que o robô falar com o Console.
            </Alert>
          )}
          {trocado && !erro && aviso && (
            <ReciboDoAviso
              key={aviso.id ?? aviso.estado}
              aviso={aviso}
              caminho={aviso.id ? reciboDoDono(aviso.id) : null}
              robo={rotuloDoRobo(robo.hardware_model)}
            />
          )}

          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
            {AVATARES.map((a) => {
              const selecionado = a === atual
              return (
                <UnstyledButton
                  key={a}
                  aria-pressed={selecionado}
                  disabled={salvando !== null}
                  onClick={() => trocar(a)}
                  style={{
                    border: `2px solid ${selecionado
                      ? 'var(--color-brand-verde)'
                      : 'var(--mantine-color-default-border)'}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    opacity: salvando !== null && salvando !== a ? 0.5 : 1,
                  }}
                >
                  <Stack gap={0}>
                    {semFoto.includes(a) ? (
                      <Stack
                        align="center"
                        justify="center"
                        style={{ aspectRatio: '2 / 1' }}
                        bg="var(--mantine-color-default-hover)"
                      >
                        <IconRobot size={32} opacity={0.4} />
                      </Stack>
                    ) : (
                      <img
                        src={foto(a)}
                        alt={`${AVATAR_LABEL[a]} no visor do robô`}
                        onError={() => setSemFoto((s) => s.includes(a) ? s : [...s, a])}
                        // O painel e' 160x80: a proporcao inteira, sem recorte, e
                        // pixel ampliado como pixel — suavizar inventaria um desenho
                        // que o visor nao mostra.
                        style={{
                          width: '100%', aspectRatio: '2 / 1', objectFit: 'contain',
                          imageRendering: 'pixelated', display: 'block',
                        }}
                      />
                    )}
                    <Text size="sm" fw={selecionado ? 600 : 400} p="xs">
                      {AVATAR_LABEL[a]}
                      {selecionado && ' · em uso'}
                    </Text>
                  </Stack>
                </UnstyledButton>
              )
            })}
          </SimpleGrid>
        </Stack>
      </DataCard>
    </Stack>
  )
}
